"use client";

import React, { ReactElement, ReactNode, useEffect, useState, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import classNames from "classnames";

import Message from "@JohnLewisPartnership/jlfs-ui-library/dist/components/message/message";
import Radio from "@JohnLewisPartnership/jlfs-ui-library/dist/components/inputs/radio/radio";
import ContentSkeleton from "@JohnLewisPartnership/jlfs-ui-library/dist/components/SkeletonLoader/SkeletonLoader";

import { TLevelProps } from "../HelpSectionCC";
import { THelpTopic } from "../../../actions/server/helpSection";
import styles from "./HelpSectionStage.module.scss";

export type THelpSectionStage = {
  stepNumber: number;
  stageTitle: string;
  categories?: THelpTopic;
  helpText?: ReactNode;
  callback: ({ selectedCategory, stepNumber }: TLevelProps) => void;
  currentStep: number;
  response: ReactElement | ReactElement[] | null;
  isResponse?: boolean;
};

const HelpSectionStage = React.forwardRef<HTMLDivElement, THelpSectionStage>(
  (
    {
      stepNumber,
      stageTitle,
      categories,
      helpText,
      callback,
      currentStep,
      response,
      isResponse,
    },
    ref
  ) => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      setLoading(false);
    }, []);

    const verticalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (verticalRef.current) {
        verticalRef.current.classList.remove(styles.animation);
        verticalRef.current.offsetHeight;  // trigger reflow
        verticalRef.current.classList.add(styles.animation);
      }
    }, [stageTitle]);

    return (
      <fieldset
        className={classNames(styles.helpSectionStage, {
          [styles.currentStep]: currentStep === stepNumber + 1,
        })}
      >
        <div className={styles.stepper}>
          <div className={styles.stepNumber}>{stepNumber + 1}</div>
          <div ref={verticalRef} className={styles.vertical}></div>
        </div>
        <div
          className={styles.stageContent}
          id={ref ? "scrollTarget" : undefined}
          key={stepNumber}
          ref={ref}
        >
          {stageTitle && (
            <legend>
              <h2
                className={classNames({
                  [styles.helpResponse]: isResponse,
                })}
              >
                {stageTitle}
              </h2>
            </legend>
          )}
          <div className={styles.categories}>
            {categories &&
              !isResponse &&
              Object.keys(categories).map((category) => {
                const thisCategory = categories[category];
                return loading ? (
                  <ContentSkeleton
                    key={thisCategory.title as string}
                    height="54px"
                  />
                ) : (
                  <Radio
                    checked={thisCategory.selected}
                    key={thisCategory.title as string}
                    label={thisCategory.title as string}
                    name={`${stepNumber}_stepNumber`}
                    value={thisCategory.id}
                    inline
                    hasBorder
                    onChange={() => {
                      callback({
                        selectedCategory: thisCategory,
                        stepNumber,
                      });
                    }}
                  />
                );
              })}
          </div>
          {helpText && (
            <div className={styles.message}>
              <Message
                title=""
                htmlContent={ReactDOMServer.renderToString(helpText)}
                type="jlm-light-green"
                icon="help"
                style="filled"
                size="large"
              ></Message>
            </div>
          )}
          {isResponse &&
            (response ? (
              response
            ) : (
              <div className={styles.skeletonResponse}>
                <ContentSkeleton height="56px" />
              </div>
            ))}
        </div>
      </fieldset>
    );
  }
);

HelpSectionStage.displayName = "HelpSectionStage";

export default HelpSectionStage;
// The useState function is a React hook and is already imported at the top of the file.
// The placeholder implementation is unnecessary and can be removed.

